import React, { createContext, useContext, useEffect, useMemo, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';
import { useSite } from './SiteContext';
import { api } from '../lib/api';
import i18n, { t } from '../lib/i18n';
import { readSavedLocale, saveLocale } from '../lib/loginPreferences';
import { setPreferredTimezone } from '../lib/datetime';
import { useLocalizedValidation } from '../hooks/useLocalizedValidation';
import { dismissAllToasts } from '../hooks/use-toast';

const LocaleContext = createContext(null);
const supported = value => ['en', 'so', 'de'].includes(value);

export const LocaleProvider = ({ children }) => {
  const { user, setUser } = useAuth();
  const { settings } = useSite();
  const { i18n: engine } = useTranslation();
  const initialized = useRef(false);
  const chosen = useRef(false);
  const guestChoice = useRef(null);
  const saveQueue = useRef(Promise.resolve());
  const sequence = useRef(0);
  const actor = useRef(user?.id);
  actor.current = user?.id;
  const locale = engine.resolvedLanguage || 'en';
  useLocalizedValidation(locale);
  useEffect(() => {
    if (user && supported(guestChoice.current)) {
      const value = guestChoice.current;
      const userId = user.id;
      guestChoice.current = null;
      i18n.changeLanguage(value);
      saveQueue.current = saveQueue.current.catch(() => {}).then(() => actor.current === userId ? api.patch('/preferences', { locale: value }) : { data: null }).then(({ data }) => {
        if (data && actor.current === userId) setUser(current => current?.id === userId ? { ...current, locale: data.locale } : current);
      }).catch(() => { if (actor.current === userId) toast.error(t('Language preference could not be saved')); });
      return;
    }
    if (user?.locale && supported(user.locale)) i18n.changeLanguage(user.locale);
    else if (!initialized.current && settings) {
      const saved = readSavedLocale(settings.gdpr?.enabled, settings.gdpr?.consentVersion);
      if (!chosen.current) i18n.changeLanguage(supported(saved) ? saved : supported(settings.language?.defaultLocale) ? settings.language.defaultLocale : 'en');
      initialized.current = true;
    }
  }, [user?.id, user?.locale, settings, setUser]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { document.documentElement.lang = locale; }, [locale]);
  useEffect(() => {
    const clearPreviousMessages = () => { toast.dismiss(); dismissAllToasts(); };
    i18n.on('languageChanged', clearPreviousMessages);
    return () => i18n.off('languageChanged', clearPreviousMessages);
  }, []);
  useEffect(() => { setPreferredTimezone(user?.timeZone); }, [user?.timeZone]);
  const changeLocale = useCallback(value => {
    if (!supported(value)) return;
    chosen.current = true;
    toast.dismiss();
    dismissAllToasts();
    const previous = locale;
    i18n.changeLanguage(value);
    saveLocale(value, settings?.gdpr?.enabled, settings?.gdpr?.consentVersion);
    if (!user) { guestChoice.current = value; return; }
    const id = ++sequence.current;
    const userId = user.id;
    saveQueue.current = saveQueue.current.catch(() => {}).then(() => actor.current === userId ? api.patch('/preferences', { locale: value }) : { data: null }).then(({ data }) => {
      if (data && id === sequence.current && actor.current === userId) setUser(current => current?.id === userId ? { ...current, locale: data.locale } : current);
    }).catch(() => {
      if (id === sequence.current && actor.current === userId) { i18n.changeLanguage(previous); toast.error(t('Language preference could not be saved')); }
    });
  }, [locale, user, settings, setUser]);
  // Context change rerenders the existing route elements without remounting forms.
  const value = useMemo(() => ({ locale, changeLocale, t }), [locale, changeLocale]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export const useLocale = () => useContext(LocaleContext);